'use client';

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

/**
 * Global site banner — rendered as the first child of `AppShell`'s flex
 * column so it sits above every route's chrome (mobile and desktop)
 * without breaking either layout mode:
 *
 *   - Mobile-chrome routes (`h-[100dvh] overflow-hidden` shell): the
 *     banner is an in-flow `flex-shrink-0` sibling of `MobileChromeTop`
 *     / `<main>` / `MobileChromeBottom`, so the flex algorithm
 *     reclaims the band from `<main>` without any JS measurement.
 *   - Desktop / scrolling routes (`min-h-screen` shell): `position:
 *     sticky top-0` keeps the strip pinned to the viewport while the
 *     document scrolls past it. This was the actual fix for the
 *     "banner disappears after full load" report — `LobbyClient`'s
 *     desktop wrapper insists on `lg:min-h-[100dvh]`, so once the
 *     lobby paints fully the page becomes `~100dvh + banner-height`
 *     tall and 28px of natural document scroll buries the banner. With
 *     sticky positioning the strip stays visible regardless of how
 *     tall the underlying route content gets.
 *
 * Dismiss persistence is cookie-driven on purpose:
 *
 *   1. An inline `<head>` script (in `layout.tsx`) reads the cookie
 *      and sets `data-banner-dismissed="1"` on `<html>` synchronously
 *      *before first paint*. A global CSS rule
 *      (`html[data-banner-dismissed="1"] .site-banner-strip { display:
 *      none }`) hides the strip in the very first frame for users
 *      who've already dismissed it — no flash, no jank, no need to
 *      defer SSR or convert the layout to a server component (which
 *      would force every statically-rendered route into per-request
 *      rendering).
 *   2. On client mount this component re-reads the same attribute and
 *      transitions to a `hidden` React state so the strip is also
 *      torn out of the DOM (not just `display: none`-hidden), keeping
 *      the React tree honest.
 *   3. Clicking the × sets the cookie, sets the attribute, and flips
 *      the state. Subsequent navigations on the same device skip the
 *      banner entirely.
 *
 * The cookie key carries a `-v1` suffix so we can invalidate every
 * existing dismissal by minting a new key the next time the banner
 * copy materially changes.
 */
const COOKIE_KEY = 'site-banner-dismissed-v1';

export function SiteBanner() {
  const [hidden, setHidden] = useState(false);

  // Mirror the inline-script attribute into React state on mount so a
  // dismissed user's banner unmounts cleanly (instead of staying
  // around as a `display: none` ghost in the DOM). The CSS rule is
  // what prevents the flash on first paint; this effect just keeps
  // the component tree consistent with the cookie.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.documentElement.dataset.bannerDismissed === '1') {
      setHidden(true);
    }
  }, []);

  if (hidden) return null;

  return (
    <div
      className={
        // `site-banner-strip` is the hook the inline-script CSS rule
        // targets — keep it in sync with the rule in `globals.css`.
        // The `h-8` literal (32px) MUST stay in sync with the
        // `--site-banner-h` value declared in `globals.css`; other
        // surfaces (notably `LobbyClient`'s desktop wrapper) subtract
        // that variable from `100dvh` so the lobby exactly fills the
        // remaining viewport instead of pushing `LobbyNav` up under
        // the sticky banner on scroll.
        'site-banner-strip sticky top-0 z-[60] flex-shrink-0 isolate ' +
        'relative flex items-center justify-center h-8 overflow-hidden ' +
        'bg-zinc-950 border-b border-white/5'
      }
    >
      {/* Glow ribbon — absolute fill, pointer-events-none so clicks
          fall through to the link / dismiss button below. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-r from-brand-red-dark/0 via-brand-red/70 to-brand-red-dark/0"
      />
      {/* Centred text. `px-10` reserves room for the `right-1`
          dismiss button so the centre line of the copy stays put as
          the viewport narrows. */}
      <span className="relative px-10 text-center text-[11px] sm:text-xs font-medium tracking-wide text-white leading-none">
        V1 grids are available on{' '}
        <a
          href="https://classic.takeover.fun"
          target="_blank"
          rel="noopener noreferrer"
          className="underline decoration-white/40 underline-offset-2 hover:decoration-white"
        >
          classic.takeover.fun
        </a>
      </span>
      <button
        type="button"
        onClick={() => {
          // 1y persistence is plenty — the banner is a transitional
          // notice and we'll mint `-v2` if we ever need to re-show it
          // before then. `SameSite=Lax` is the default-friendly
          // choice; the cookie isn't auth-bearing.
          document.cookie = `${COOKIE_KEY}=1; path=/; max-age=31536000; SameSite=Lax`;
          document.documentElement.dataset.bannerDismissed = '1';
          setHidden(true);
        }}
        aria-label="Dismiss banner"
        className="absolute right-1 top-1/2 -translate-y-1/2 grid place-items-center h-6 w-6 rounded-md text-white/80 hover:text-white hover:bg-white/15 transition-colors"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
